import type {MouseEventHandler, ReactElement} from 'react';
import React from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {useUpdateEffect} from 'ahooks';
import {ContextualMenu} from '@Components/contextual-menu';
import {Text, TextSize} from '@Components/text';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItem7} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE, ControlledListItemSize} from '@Components/controlled-list/controlled-list.types';
import {GroupHorizontalAlignment, GroupVeticalAlignment} from '@PosterWhiteboard/page/alignment.class';
import {GroupItemsSpacingOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/group-items-spacing-options';
import styles from './poster-editing-item-symmetry-panel.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';

interface PosterEditingItemPositionPanelProps {
  panelId: string;
  showBackButton?: boolean;
  onBackButton?: MouseEventHandler;
  onClose?(): void;
}

enum OPTION_TYPES {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

export default function PosterEditingItemSymmetryPanel({showBackButton = false, onBackButton = (): void => {}, ...props}: PosterEditingItemPositionPanelProps): ReactElement {
  const onClose = props.onClose?.bind(null) ?? useDefaultPanelClose(props.panelId);
  const activeItemId = useSingleActivePosterItemId();
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId]);

  const getHeader = (): ReactElement => {
    return <PanelHeader showBackButton={showBackButton} onBackButton={onBackButton} onClose={onClose} title={window.i18next.t('pmwjs_symmetry')} />;
  };

  const getItems = (): ControlledListItem7[] => {
    return [
      {
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        id: `${OPTION_TYPES.HORIZONTAL}-${GroupHorizontalAlignment.LEFT}`,
        icon: 'icon-left-align-shapes',
        text: window.i18next.t('pmwjs_left'),
        className: styles.items,
        listSize: ControlledListItemSize.MEDIUM,
      },
      {
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        id: `${OPTION_TYPES.VERTICAL}-${GroupVeticalAlignment.TOP}`,
        icon: 'icon-top-align-shapes',
        text: window.i18next.t('pmwjs_top'),
        className: styles.items,
        listSize: ControlledListItemSize.MEDIUM,
      },
      {
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        id: `${OPTION_TYPES.VERTICAL}-${GroupVeticalAlignment.CENTER}`,
        icon: 'icon-middle-align-shapes',
        className: styles.items,
        text: window.i18next.t('pmwjs_center'),
        listSize: ControlledListItemSize.MEDIUM,
      },
      {
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        id: `${OPTION_TYPES.HORIZONTAL}-${GroupHorizontalAlignment.CENTER}`,
        icon: 'icon-center-align-shapes',
        text: window.i18next.t('pmwjs_middle'),
        className: styles.items,
        listSize: ControlledListItemSize.MEDIUM,
      },
      {
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        id: `${OPTION_TYPES.HORIZONTAL}-${GroupHorizontalAlignment.RIGHT}`,
        icon: 'icon-right-align-shapes',
        text: window.i18next.t('pmwjs_right'),
        className: styles.items,
        listSize: ControlledListItemSize.MEDIUM,
      },
      {
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        id: `${OPTION_TYPES.VERTICAL}-${GroupVeticalAlignment.BOTTOM}`,
        icon: 'icon-bottom-align-shapes',
        className: styles.items,
        text: window.i18next.t('pmwjs_bottom'),
        listSize: ControlledListItemSize.MEDIUM,
      },
    ];
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu id="item-mobile-position-options" includeLeftRightPadding={false} contentClassName="spacing-p-l-4 spacing-p-r-4 spacing-p-b-4 spacing-p-t-1 _fit-height">
        <GroupItemsSpacingOptions
          optionsContainerClassName={styles.optionsContainer}
          headingComponent={<Text className="spacing-m-b-2" val={window.i18next.t('pmwjs_spacing')} size={TextSize.XXSMALL} bold />}
        />
        <Text className="spacing-m-b-2" val={window.i18next.t('pmwjs_alignment')} size={TextSize.XXSMALL} bold />
        <ControlledList className={`flexbox spacing-m-0 ${styles.list}`} onClick={onClickAlignmentOptions} items={getItems()} />
      </ContextualMenu>
    );
  };

  const applyVerticalAlignment = (option: GroupVeticalAlignment): void => {
    currentPage?.activeSelection?.alignment?.verticalAlignment(option);
  };

  const applyHorizontalAlignment = (option: GroupHorizontalAlignment): void => {
    currentPage?.activeSelection?.alignment?.horizontalAlignment(option);
  };

  const onClickAlignmentOptions = (clickedId: string): void => {
    const type = clickedId.split('-')[0];
    const option = clickedId.split('-')[1];
    if (type === OPTION_TYPES.VERTICAL) {
      applyVerticalAlignment(option as GroupVeticalAlignment);
      return;
    }

    applyHorizontalAlignment(option as GroupHorizontalAlignment);
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
}
